// src/store/auth_slice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { auth_service } from '@services/auth_service';
import type { User, LoginCredentials } from '@/types/common_types';
import type { RootState } from './index';

/**
 * Registration data interface
 */
export interface RegisterData {
  email: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role?: User['role'];
}

/**
 * Authentication state interface
 */
interface AuthState {
  user: User | null;
  is_authenticated: boolean;
  is_loading: boolean;
  error: string | null;
  token: string | null;
  refresh_token: string | null;
  last_activity: number;
  session_expired: boolean;
}

/**
 * Initial state
 */
const initial_state: AuthState = {
  user: auth_service.get_user() as User | null,
  is_authenticated: auth_service.is_authenticated(),
  is_loading: false,
  error: null,
  token: auth_service.get_access_token(),
  refresh_token: auth_service.get_refresh_token(),
  last_activity: Date.now(),
  session_expired: false,
};

// ============ ASYNC THUNKS ============

/**
 * Login user with credentials
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await auth_service.login(credentials);
      return response as { user: User; access: string; refresh: string };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

/**
 * Register new user
 */
export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await auth_service.register(data);
      return response as { user: User; access: string; refresh: string };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

/**
 * Logout user
 */
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await auth_service.logout();
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

/**
 * Get current user profile
 */
export const get_current_user = createAsyncThunk(
  'auth/get_current_user',
  async (_, { rejectWithValue }) => {
    try {
      const user = await auth_service.get_current_user();
      return user as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch user');
    }
  }
);

/**
 * Update user profile
 */
export const update_profile = createAsyncThunk(
  'auth/update_profile',
  async (data: Partial<User>, { rejectWithValue }) => {
    try {
      const user = await auth_service.update_profile(data as any);
      return user as User;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

/**
 * Change password
 */
export const change_password = createAsyncThunk(
  'auth/change_password',
  async (
    data: { old_password: string; new_password: string; new_password_confirm: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await auth_service.change_password(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to change password');
    }
  }
);

/**
 * Refresh authentication token
 */
export const refresh_token = createAsyncThunk(
  'auth/refresh_token',
  async (_, { rejectWithValue }) => {
    try {
      const token = await auth_service.refresh_token();
      return token;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh token');
    }
  }
);

// ============ SLICE ============

const auth_slice = createSlice({
  name: 'auth',
  initialState: initial_state,
  reducers: {
    /**
     * Clear authentication error
     */
    clear_auth_error: (state) => {
      state.error = null;
    },

    /**
     * Update last activity timestamp
     */
    update_last_activity: (state) => {
      state.last_activity = Date.now();
      state.session_expired = false;
    },

    /**
     * Mark session as expired
     */
    mark_session_expired: (state) => {
      state.session_expired = true;
    },

    /**
     * Set user manually (for scenarios like token refresh)
     */
    set_user: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.is_authenticated = true;
    },

    /**
     * Clear authentication state
     */
    clear_auth_state: (state) => {
      state.user = null;
      state.is_authenticated = false;
      state.token = null;
      state.refresh_token = null;
      state.error = null;
      state.session_expired = false;
    },
  },
  extraReducers: (builder) => {
    // ===== LOGIN =====
    builder
      .addCase(login.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.is_loading = false;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.refresh_token = action.payload.refresh;
        state.is_authenticated = true;
        state.error = null;
        state.last_activity = Date.now();
        state.session_expired = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
        state.is_authenticated = false;
        state.user = null;
      });

    // ===== REGISTER =====
    builder
      .addCase(register.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.is_loading = false;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.refresh_token = action.payload.refresh;
        state.is_authenticated = true;
        state.error = null;
        state.last_activity = Date.now();
      })
      .addCase(register.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== LOGOUT =====
    builder
      .addCase(logout.pending, (state) => {
        state.is_loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.is_loading = false;
        state.user = null;
        state.token = null;
        state.refresh_token = null;
        state.is_authenticated = false;
        state.error = null;
        state.session_expired = false;
      })
      .addCase(logout.rejected, (state) => {
        state.is_loading = false;
        // Still clear auth state even if logout API call fails
        state.user = null;
        state.token = null;
        state.refresh_token = null;
        state.is_authenticated = false;
      });

    // ===== GET CURRENT USER =====
    builder
      .addCase(get_current_user.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(get_current_user.fulfilled, (state, action) => {
        state.is_loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(get_current_user.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== UPDATE PROFILE =====
    builder
      .addCase(update_profile.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(update_profile.fulfilled, (state, action) => {
        state.is_loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(update_profile.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== CHANGE PASSWORD =====
    builder
      .addCase(change_password.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(change_password.fulfilled, (state) => {
        state.is_loading = false;
        state.error = null;
      })
      .addCase(change_password.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== REFRESH TOKEN =====
    builder
      .addCase(refresh_token.pending, (state) => {
        state.is_loading = true;
      })
      .addCase(refresh_token.fulfilled, (state, action) => {
        state.is_loading = false;
        state.token = action.payload;
        state.last_activity = Date.now();
        state.session_expired = false;
      })
      .addCase(refresh_token.rejected, (state) => {
        state.is_loading = false;
        state.user = null;
        state.token = null;
        state.refresh_token = null;
        state.is_authenticated = false;
        state.session_expired = true;
      });
  },
});

// ============ ACTIONS ============

export const {
  clear_auth_error,
  update_last_activity,
  mark_session_expired,
  set_user,
  clear_auth_state,
} = auth_slice.actions;

// ============ SELECTORS ============

export const select_auth = (state: RootState) => state.auth;
export const select_user = (state: RootState) => state.auth.user;
export const select_is_authenticated = (state: RootState) => state.auth.is_authenticated;
export const select_auth_loading = (state: RootState) => state.auth.is_loading;
export const select_auth_error = (state: RootState) => state.auth.error;
export const select_user_role = (state: RootState) => state.auth.user?.role;
export const select_session_expired = (state: RootState) => state.auth.session_expired;

/**
 * Check if user has specific role
 */
export const select_has_role = (role: User['role']) => (state: RootState) => {
  return state.auth.user?.role === role;
};

/**
 * Check if user has any of the specified roles
 */
export const select_has_any_role = (roles: User['role'][]) => (state: RootState) => {
  return state.auth.user ? roles.includes(state.auth.user.role) : false;
};

/**
 * Get user's full name
 */
export const select_user_full_name = (state: RootState) => {
  const user = state.auth.user;
  return user ? `${user.first_name} ${user.last_name}` : '';
};

/**
 * Check if session is active (not expired and authenticated)
 */
export const select_is_session_active = (state: RootState) => {
  return state.auth.is_authenticated && !state.auth.session_expired;
};

// ============ EXPORT REDUCER ============

export default auth_slice.reducer;